import React, { useRef } from "react";
import { Worker, Viewer } from "@react-pdf-viewer/core";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { regular, solid } from "@fortawesome/fontawesome-svg-core/import.macro";
import * as pdfjs from "pdfjs-dist";
import "@react-pdf-viewer/core/lib/styles/index.css";
import "@react-pdf-viewer/zoom/lib/styles/index.css";
import "./DocumentPreview.scss";
import useDesktopChecker from "hooks/useDesktopChecker";
import { useDocumentType } from "hooks/useDocumentType";
import { useZoom } from "hooks/useZoom";
import { useDrag } from "hooks/useDrag";
import { usePageNavigation } from "hooks/usePageNavigation";
import { splitAndCapitalizeWords, isNonEmptyString } from "utilities/string";

interface DocumentPreviewProps {
  fileUrl: string;
  fileName?: string;
  hasError?: boolean;
  handleDownload?: () => void;
  useDrawerWrapper?: boolean; // New optional prop
  invalidReason?: string;
  invalidDescription?: string;
}

// PDF.js worker URL
const PDF_WORKER_URL = `https://unpkg.com/pdfjs-dist@${pdfjs.version}/build/pdf.worker.min.js`;

const DocumentPreview: React.FC<DocumentPreviewProps> = ({
  fileUrl,
  fileName,
  hasError,
  handleDownload,
  useDrawerWrapper = true, // Default to true for backward compatibility
  invalidReason,
  invalidDescription,
}) => {
  const isDesktop = useDesktopChecker();
  const viewerRef = useRef<HTMLDivElement>(null);

  // Custom hooks for complex logic
  const { fileType, numPages, setNumPages } = useDocumentType(fileUrl, fileName);
  const { scale, defaultZoomScale, zoomPluginInstance, handleZoomIn, handleZoomOut, isZoomOutDisabled } = useZoom(fileType);
  const { input, setInput, currentPage, pageNavigationPluginInstance, handleDocumentLoad, handlePageChange, handleInputKeyDown } = usePageNavigation(fileType, numPages);
  const { isDragging, handleMouseDown, handleTouchStart, handleKeyDown, handleDragStart } = useDrag(viewerRef, scale, defaultZoomScale, fileType);

  // Update numPages when document loads
  const onDocumentLoad = (e: { doc: { numPages: number } }) => {
    setNumPages(e.doc.numPages);
    handleDocumentLoad(e);
  };

  // Render content based on file type
  const renderContent = () => {
    if (fileType === "pdf") {
      return (
        <Worker workerUrl={PDF_WORKER_URL}>
          <Viewer
            fileUrl={fileUrl}
            defaultScale={defaultZoomScale}
            plugins={[zoomPluginInstance, pageNavigationPluginInstance]}
            onDocumentLoad={onDocumentLoad}
            onPageChange={handlePageChange}
          />
        </Worker>
      );
    } else if (fileType === "image") {
      return (
        <img
          src={fileUrl}
          alt="Document"
          className={`document-preview__image ${
            isDragging
              ? "document-preview__image--dragging"
              : "document-preview__image--interactive"
          }`}
          style={{
            transform: `scale(${scale})`,
          }}
        />
      );
    } else {
      return (
        <div className="document-preview__unsupported">
          Unsupported file type
        </div>
      );
    }
  };

  // Get viewer className based on scale and dragging state
  const getViewerClassName = () => {
    const baseClass = "document-preview__viewer";
    if (scale <= defaultZoomScale) {
      return `${baseClass} document-preview__viewer--default`;
    }
    if (isDragging) {
      return `${baseClass} document-preview__viewer--draggable document-preview__viewer--dragging`;
    }
    return `${baseClass} document-preview__viewer--draggable`;
  };

  // Render mobile page indicator
  const renderMobilePageIndicator = () => {
    return !isDesktop && fileType === "pdf" && numPages ? (
      <div className="document-preview__mobile-page-indicator">
        Page {currentPage} of {numPages}
      </div>
    ) : null;
  };

  // Render navigation bar
  const renderNavigationBar = () => (
    <div className="document-preview__nav-bar">
      {/* Page navigation - only show for PDFs and images on desktop */}
      {isDesktop && (fileType === "pdf" || fileType === "image") && (
        <div className="document-preview__page-nav">
          <label className="document-preview__page-label" htmlFor="page-input">Page</label>
          <input
            id="page-input"
            type="number"
            value={input}
            onChange={(e) => setInput(e.target.value)}
            onKeyDown={handleInputKeyDown}
            onBlur={() => setInput("")}
            placeholder={`${currentPage}`}
            className="document-preview__page-input"
            min="1"
            max={numPages || undefined}
            disabled={fileType === "image"}
          />
          <span className="document-preview__page-separator">
            / {numPages || "?"}
          </span>
          <div className="document-preview__divider"></div>
          <button
            onClick={handleZoomOut}
            disabled={isZoomOutDisabled}
            className={`document-preview__zoom-btn ${
              isZoomOutDisabled ? "document-preview__zoom-btn--disabled" : ""
            }`}
            title="Zoom Out (10%)"
          >
            −
          </button>
          <FontAwesomeIcon
            icon={solid("magnifying-glass")}
            className="document-preview__zoom-icon"
          />
          <button
            onClick={handleZoomIn}
            className="document-preview__zoom-btn"
            title="Zoom In (10%)"
          >
            +
          </button>
        </div>
      )}
      {/* Download button - always visible */}
      <div className="document-preview__zoom-controls">
        <button
          className={`drawer__upload-btn ${
            hasError ? "drawer__upload-btn--disabled" : ""
          }`}
          onClick={() => handleDownload?.()}
          disabled={hasError}
          aria-label="download"
        >
          <FontAwesomeIcon
            className="drawer__download-btn"
            icon={regular("arrow-down-to-bracket")}
          />
          <div className="drawer__placeholder-text">Download</div>
        </button>
      </div>
    </div>
  );

  const getComplexViewerClassName = () => {
    const baseClass = "document-preview__viewer";
    if (scale > defaultZoomScale) {
      if (isDragging) {
        return `${baseClass} document-preview__viewer--draggable document-preview__viewer--dragging`;
      }
      return `${baseClass} document-preview__viewer--draggable`;
    }
    return `${baseClass} document-preview__viewer--default`;
  };

  // Render content viewer
  const renderContentViewer = (useComplexClassName = false) => {
    const isZoomed = scale > defaultZoomScale;
    const ariaLabel = isZoomed
      ? "Document viewer - zoomed content: use mouse to drag, arrow keys to scroll, or touch to pan"
      : "Document viewer";

    const commonProps = {
      ref: viewerRef,
      className: useComplexClassName ? getComplexViewerClassName() : getViewerClassName(),
      style: {'--scale-factor': scale} as React.CSSProperties,
      'aria-label': ariaLabel,
      onMouseDown: handleMouseDown,
      onTouchStart: handleTouchStart,
      onKeyDown: handleKeyDown,
      onDragStart: handleDragStart,
    };

    if (isZoomed) {
      return (
        <div
          {...commonProps}
          role="application"
          aria-describedby="zoom-instructions"
        >
          <div id="zoom-instructions" className="document-preview__sr-only">
            Use mouse to drag, arrow keys to scroll, or touch to pan the zoomed content
          </div>
          {renderContent()}
        </div>
      );
    }

    return (
      <section {...commonProps}>
        {renderContent()}
      </section>
    );
  };

  const renderInvalidMessage = () => {
    if (!isNonEmptyString(invalidReason) || !isNonEmptyString(invalidDescription)) return null;

    return (
      <div className="document-preview__invalid-content">
        <div className="document-preview__invalid-header">
          <FontAwesomeIcon className="document-preview__invalid-icon" icon={regular("triangle-exclamation")} />
          <div className="document-preview__invalid-reason">{splitAndCapitalizeWords(invalidReason ?? "")}</div>
        </div>
        <div className="document-preview__invalid-description">{invalidDescription}</div>
      </div>
    );
  };

  // Render layout based on wrapper preference
  const renderLayout = () => {
    const content = (
      <>
        <div className="document-preview__container">
          {renderNavigationBar()}
          {renderInvalidMessage()}
          {renderMobilePageIndicator()}
          {renderContentViewer(useDrawerWrapper)}
        </div>
      </>
    );

    return useDrawerWrapper ? (
      <div className="document-preview">
        {content}
      </div>
    ) : content;
  };

  return renderLayout();
};

export default DocumentPreview;
